import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeftIcon, CheckIcon } from '@heroicons/react/24/outline'
import { useOrdine } from '@/hooks/useOrdini'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { AlertTriangle, Factory } from 'lucide-react'
import SpedizioneWizardStep1 from '@/components/ordini/wizard/SpedizioneWizardStep1'
import SpedizioneWizardStep2 from '@/components/ordini/wizard/SpedizioneWizardStep2'
import SpedizioneWizardStep3 from '@/components/ordini/wizard/SpedizioneWizardStep3'
import SpedizioneWizardStep4 from '@/components/ordini/wizard/SpedizioneWizardStep4'
import { createSpedizioneWizard, verificaSpedizioneOrdine } from '@/api/ordini'
import { buildAssemblyUrl, normalizeAssemblySteps } from '@/utils/assemblyPlan'
import { toast } from 'sonner'

/**
 * Wizard creazione spedizione completa.
 * 
 * FASE 7.4: Wizard Spedizioni
 * 
 * 4 Step:
 * 1. Dati spedizione (corriere, indirizzo, tracking)
 * 2. Colli e destinatari
 * 3. Assegnazione righe ordine ai colli
 * 4. Riepilogo e conferma
 * 
 * Features:
 * - Validazione step by step
 * - Navigazione avanti/indietro
 * - Salvataggio transazionale finale
 * - Auto-compilazione indirizzo da cliente
 */
export default function SpedizioneWizard() {
  const { ordineId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: ordine, isLoading } = useOrdine(ordineId)

  const [currentStep, setCurrentStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCheckingPrerequisiti, setIsCheckingPrerequisiti] = useState(true)
  const [prerequisitiResult, setPrerequisitiResult] = useState(null)

  // Stato wizard
  const [wizardData, setWizardData] = useState({
    // Step 1: Dati spedizione
    destinatario_id: null,
    indirizzo_destinatario_id: null,
    corriere: '',
    tracking_number: '',
    stato: 'preparazione',
    indirizzo_via: '',
    indirizzo_civico: '',
    indirizzo_cap: '',
    indirizzo_citta: '',
    indirizzo_provincia: '',
    indirizzo_nazione: 'IT',
    indirizzo_presso: '',
    indirizzo_note: '',
    salva_indirizzo_cliente: false,
    data_spedizione: null,
    data_consegna_prevista: null,
    costo_spedizione_cents: 0,
    note: '',

    // Step 2: Colli
    colli: [],

    // Step 3: Assegnazioni (gestito internamente da Step3)
  })

  // Auto-compila indirizzo da cliente quando ordine carica
  useEffect(() => {
    if (ordine && ordine.cliente) {
      // Cerca indirizzo default, altrimenti prendi il primo disponibile
      const indirizzoDefault = ordine.cliente.indirizzi?.find(i => i.default) 
        || ordine.cliente.indirizzi?.[0]
      if (indirizzoDefault) {
        setWizardData(prev => prev.corriere === 'ritiro_in_sede' ? prev : ({
          ...prev,
          indirizzo_via: indirizzoDefault.via || '',
          indirizzo_civico: indirizzoDefault.civico || '',
          indirizzo_cap: indirizzoDefault.cap || '',
          indirizzo_citta: indirizzoDefault.citta || '',
          indirizzo_provincia: indirizzoDefault.provincia || '',
          indirizzo_nazione: indirizzoDefault.nazione || 'IT',
          indirizzo_presso: indirizzoDefault.destinatario || '', // Destinatario diverso dal cliente
          indirizzo_note: indirizzoDefault.note || '',
        }))
      }
    }
  }, [ordine])

  const verificaPrerequisiti = useCallback(async () => {
    setIsCheckingPrerequisiti(true)
    try {
      const response = await verificaSpedizioneOrdine(ordineId)
      setPrerequisitiResult(response?.data || null)
    } catch (error) {
      const msg =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        'Errore durante la verifica dei prerequisiti spedizione.'
      toast.error(typeof msg === 'string' ? msg : 'Errore verifica prerequisiti')
      setPrerequisitiResult({
        ok: false,
        reason: 'errore_verifica',
        message: 'Impossibile verificare i prerequisiti spedizione.',
        mancanze: [],
        assembly_plan: [],
      })
    } finally {
      setIsCheckingPrerequisiti(false)
    }
  }, [ordineId])

  useEffect(() => {
    if (!ordineId || !ordine) return
    verificaPrerequisiti()
  }, [ordineId, ordine?.id, verificaPrerequisiti])

  const steps = [
    { number: 1, title: 'Dati Spedizione', description: 'Corriere e indirizzo' },
    { number: 2, title: 'Colli', description: 'Destinatari e pesi' },
    { number: 3, title: 'Assegnazione Righe', description: 'Articoli nei colli' },
    { number: 4, title: 'Riepilogo', description: 'Verifica e conferma' },
  ]

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleStepComplete = (stepData) => {
    setWizardData(prev => ({ ...prev, ...stepData }))
    handleNext()
  }

  const handleSubmit = async (finalData) => {
    setIsSubmitting(true)
    try {
      const payload = {
        ordine_id: parseInt(ordineId),
        ...finalData,
      }

      console.log('Payload inviato al backend:', JSON.stringify(payload, null, 2))
      await createSpedizioneWizard(payload)
      queryClient.invalidateQueries({ queryKey: ['ordini'], refetchType: 'all' })
      queryClient.invalidateQueries({ queryKey: ['spedizioni'], refetchType: 'all' })
      queryClient.invalidateQueries({ queryKey: ['giacenze-prodotti'], refetchType: 'all' })
      queryClient.invalidateQueries({ queryKey: ['giacenze-prodotti-sottoscorta'], refetchType: 'all' })
      queryClient.invalidateQueries({ queryKey: ['giacenze-componenti'], refetchType: 'all' })
      queryClient.invalidateQueries({ queryKey: ['giacenze-componenti-sottoscorta'], refetchType: 'all' })
      queryClient.invalidateQueries({ queryKey: ['giacenze-box'], refetchType: 'all' })
      queryClient.invalidateQueries({ queryKey: ['giacenza-prodotto'], refetchType: 'all' })
      queryClient.invalidateQueries({ queryKey: ['giacenza-componente'], refetchType: 'all' })
      queryClient.invalidateQueries({ queryKey: ['prodotti'], refetchType: 'all' })
      queryClient.invalidateQueries({ queryKey: ['tipologie-box'], refetchType: 'all' })
      toast.success('Spedizione creata con successo!')
      navigate(`/ordini/${ordineId}`)
    } catch (error) {
      console.error('Errore creazione spedizione:', error)
      console.error('Dettaglio errore backend:', error.response?.data)
      const errorMsg = error.response?.data?.righe_ordine?.[0] || 
                       error.response?.data?.detail ||
                       JSON.stringify(error.response?.data) ||
                       'Errore durante la creazione della spedizione'
      toast.error(errorMsg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const assemblySteps = normalizeAssemblySteps(prerequisitiResult)
  const overbookingRows = Array.isArray(prerequisitiResult?.mancanze_overbooking)
    ? prerequisitiResult.mancanze_overbooking
    : []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Caricamento ordine...</p>
      </div>
    )
  }

  if (!ordine) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-destructive">Ordine non trovato</p>
      </div>
    )
  }

  if (isCheckingPrerequisiti) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/ordini/${ordineId}`)}
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Crea Spedizione</h1>
            <p className="text-muted-foreground">Verifica prerequisiti in corso...</p>
          </div>
        </div>
        <Card>
          <CardContent className="py-10">
            <div className="flex items-center justify-center gap-3 text-muted-foreground">
              <div className="h-5 w-5 rounded-full border-2 border-current border-t-transparent animate-spin" />
              Controllo disponibilità box assemblate...
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (prerequisitiResult && !prerequisitiResult.ok) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/ordini/${ordineId}`)}
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Spedizione bloccata</h1>
            <p className="text-muted-foreground">
              Ordine {ordine.numero} - Completa prima l&apos;assemblaggio richiesto.
            </p>
          </div>
        </div>

        <Card className="border-amber-300 bg-amber-50/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5" />
              Piano assemblaggio multi-step
            </CardTitle>
            <CardDescription>
              Segui gli step in ordine. Dopo l&apos;assemblaggio, torna qui e clicca &quot;Ricontrolla&quot;.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {assemblySteps.length > 0 ? (
              assemblySteps.map((step) => (
                <div key={`${step.step}-${step.tipologia_box_id}`} className="rounded-lg border bg-background p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="font-medium">
                        Step {step.step}: {step.tipologia_box_nome}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Disponibili: {step.assemblate_disponibili} • Richieste totali: {step.richieste_totali}
                      </p>
                    </div>
                    <Badge variant={step.da_assemblare > 0 ? 'destructive' : 'secondary'}>
                      Da assemblare: {step.da_assemblare}
                    </Badge>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() =>
                        navigate(
                          buildAssemblyUrl({
                            step,
                            ordineNumero: ordine.numero,
                            returnTo: `/ordini/${ordineId}/spedizione/wizard`,
                          })
                        )
                      }
                    >
                      <Factory className="h-4 w-4 mr-2" />
                      Apri assemblaggio step
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {prerequisitiResult.message || 'Prerequisiti spedizione non rispettati.'}
                </p>
                {overbookingRows.length > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-1">
                    <p className="text-sm font-medium text-amber-800">Overbooking attivo</p>
                    {overbookingRows.map((row) => (
                      <p key={`${row.tipologia_box_id}-${row.riga_ordine_id}`} className="text-xs text-amber-800">
                        {row.tipologia_box_nome}: effettiva {row.quantita_effettiva} (disp {row.assemblate_disponibili}, ris {row.quantita_riservata})
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => navigate(`/ordini/${ordineId}`)}>
                Torna ordine
              </Button>
              <Button onClick={verificaPrerequisiti}>
                Ricontrolla prerequisiti
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/ordini/${ordineId}`)}
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Crea Spedizione</h1>
          <p className="text-muted-foreground">
            Ordine {ordine.numero} - {ordine.cliente?.nome_completo}
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            {steps.map((step, idx) => (
              <div key={step.number} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                      currentStep > step.number
                        ? 'bg-primary border-primary text-primary-foreground'
                        : currentStep === step.number
                        ? 'border-primary text-primary'
                        : 'border-muted-foreground text-muted-foreground'
                    }`}
                  >
                    {currentStep > step.number ? (
                      <CheckIcon className="h-5 w-5" />
                    ) : (
                      <span className="font-semibold">{step.number}</span>
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    <div className="text-sm font-medium">{step.title}</div>
                    <div className="text-xs text-muted-foreground">{step.description}</div>
                  </div>
                </div>
                {idx < steps.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 mx-4 ${
                      currentStep > step.number ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Step Content */}
      <div className="min-h-[500px]">
        {currentStep === 1 && (
          <SpedizioneWizardStep1
            data={wizardData}
            ordine={ordine}
            onComplete={handleStepComplete}
            onBack={() => navigate(`/ordini/${ordineId}`)}
          />
        )}
        {currentStep === 2 && (
          <SpedizioneWizardStep2
            data={wizardData}
            ordine={ordine}
            onComplete={handleStepComplete}
            onBack={handleBack}
          />
        )}
        {currentStep === 3 && (
          <SpedizioneWizardStep3
            data={wizardData}
            ordine={ordine}
            onComplete={handleStepComplete}
            onBack={handleBack}
          />
        )}
        {currentStep === 4 && (
          <SpedizioneWizardStep4
            data={wizardData}
            ordine={ordine}
            onSubmit={handleSubmit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
            onUpdateColli={(newColli) => setWizardData(prev => ({ ...prev, colli: newColli }))}
          />
        )}
      </div>
    </div>
  )
}
